import { useState, type FormEvent } from 'react';
import { ApiFailure, api, describeError } from '../api';
import { getDeviceId, useSession, type AuthResult } from '../session';

type Mode = 'otp' | 'password' | 'register' | 'reset';

interface OtpResponse {
  sent: boolean;
  expires_in?: number;
  resend_after?: number;
  debug_code?: string;
}

export function LoginPage() {
  const { login, register, otpLogin, oauthLogin, applyAuthResult, env } = useSession();
  const [mode, setMode] = useState<Mode>('otp');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [codeInfo, setCodeInfo] = useState<string | null>(null);
  const [idToken, setIdToken] = useState('');
  const [provider, setProvider] = useState<'apple' | 'google'>('google');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof ApiFailure ? `${err.error.code} — ${err.error.message}${err.requestId ? ` (request ${err.requestId})` : ''}${JSON.stringify({ ...err.error, code: undefined, message: undefined }) !== '{}' ? ` ${JSON.stringify({ ...err.error, code: undefined, message: undefined })}` : ''}` : describeError(err));
    } finally {
      setBusy(false);
    }
  };

  const requestCode = (purpose: 'login' | 'reset_password') =>
    run(async () => {
      const path = purpose === 'login' ? '/api/auth/otp/request' : '/api/auth/password/reset/request';
      const body = purpose === 'login' ? { email, purpose } : { email };
      const { data } = await api<OtpResponse>(path, { method: 'POST', body, auth: false });
      if (data.debug_code) setCode(data.debug_code);
      setCodeInfo(`验证码已发送（${data.expires_in ?? '?'} s 内有效，${data.resend_after ?? '?'} s 后可重发）${data.debug_code ? ` · staging mock_email 已自动填入 debug_code ${data.debug_code}` : ''}`);
    });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      if (mode === 'password') await login(email, password);
      else if (mode === 'register') await register(email, password, name);
      else if (mode === 'otp') {
        const user = await otpLogin(email, code);
        if (user.is_new) setMessage('新账号已创建（is_new）');
      } else if (mode === 'reset') {
        const { data } = await api<AuthResult>('/api/auth/password/reset', { method: 'POST', body: { email, code, new_password: password, device_id: getDeviceId() }, auth: false });
        applyAuthResult(data);
      }
    });
  };

  const tab = (m: Mode, label: string) => (
    <button type="button" key={m} className={`rounded-lg px-3 py-1 text-xs ${mode === m ? 'bg-indigo-600 text-white' : 'bg-zinc-800 text-zinc-300 hover:text-white'}`} onClick={() => { setMode(m); setError(null); setMessage(null); setCodeInfo(null); }}>
      {label}
    </button>
  );

  return (
    <div className="mx-auto mt-10 max-w-md space-y-4">
      <div className="card">
        <h1 className="mb-1 text-lg font-semibold">登录 PocketTavern</h1>
        <p className="mb-3 text-xs text-zinc-400">环境：{env} · 设备 id <span className="font-mono">{getDeviceId().slice(0, 13)}…</span></p>
        <div className="mb-4 flex flex-wrap gap-2">
          {tab('otp', '验证码登录')}
          {tab('password', '密码登录')}
          {tab('register', '密码注册')}
          {tab('reset', '忘记密码')}
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="label">邮箱</label>
            <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="username" />
          </div>
          {(mode === 'otp' || mode === 'reset') && (
            <div>
              <label className="label">验证码（6 位）</label>
              <div className="flex gap-2">
                <input className="input font-mono" value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" pattern="\d{6}" required />
                <button type="button" className="btn-ghost whitespace-nowrap" disabled={busy || !email} onClick={() => void requestCode(mode === 'otp' ? 'login' : 'reset_password')}>发送验证码</button>
              </div>
              {codeInfo && <p className="mt-1 text-[11px] text-zinc-400">{codeInfo}</p>}
            </div>
          )}
          {(mode === 'password' || mode === 'register' || mode === 'reset') && (
            <div>
              <label className="label">{mode === 'reset' ? '新密码（≥ 8 位）' : '密码（≥ 8 位）'}</label>
              <input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={mode === 'password' ? 1 : 8} autoComplete={mode === 'password' ? 'current-password' : 'new-password'} />
            </div>
          )}
          {mode === 'register' && (
            <div>
              <label className="label">昵称</label>
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
          )}
          {error && <p className="rounded-lg bg-red-950/60 p-2 text-xs text-red-300">{error}</p>}
          {message && <p className="rounded-lg bg-emerald-950/60 p-2 text-xs text-emerald-200">{message}</p>}
          <button className="btn" disabled={busy}>
            {busy ? '…' : mode === 'otp' ? '验证码登录（未注册自动建号）' : mode === 'password' ? '登录' : mode === 'register' ? '注册' : '重设密码并登录'}
          </button>
        </form>
        <p className="mt-3 text-[11px] text-zinc-500">staging 已接 Resend 真发（发件人 no-reply@pockettavern.app），请用能收信的邮箱；只有管理员在系统页开 <span className="font-mono">mock_email</span> 时响应才带 <span className="font-mono">debug_code</span>；production 永不返回。</p>
      </div>

      <div className="card">
        <h2 className="mb-1 font-semibold">Apple / Google 登录 <span className="text-xs text-zinc-400">POST /api/auth/oauth/{'{provider}'}</span></h2>
        <p className="mb-3 text-[11px] text-zinc-500">console 拿不到原生 SDK 的 id_token：把真机 / Google Identity Services 返回的 id_token 粘贴到这里校验。凭据未配置时返回 <span className="font-mono">AUTH_OAUTH_TOKEN_INVALID reason=provider_not_configured</span>。</p>
        <div className="grid gap-2 md:grid-cols-[120px_1fr]">
          <select className="input" value={provider} onChange={(e) => setProvider(e.target.value as 'apple' | 'google')}>
            <option value="google">google</option>
            <option value="apple">apple</option>
          </select>
          <textarea className="input font-mono text-[11px]" rows={3} placeholder="eyJhbGciOiJSUzI1NiIsImtpZCI6..." value={idToken} onChange={(e) => setIdToken(e.target.value)} />
        </div>
        <button className="btn mt-2" disabled={busy || !idToken} onClick={() => void run(async () => { const u = await oauthLogin(provider, idToken.trim(), name || undefined); setMessage(u.is_new ? '第三方登录：新账号已创建' : '第三方登录成功'); })}>
          用 id_token 登录
        </button>
      </div>
    </div>
  );
}
